import {
  Mode,
  SearchFilter,
  UserRef,
  ZonedDateTimeRange,
} from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import { MoneySearchFilter } from '../MoneySearchFilter';
import { ReferenceNumber } from '../ReferenceNumber';
import { UomSearchFilter } from '../search/filter/UomSearchFilter';
import { SlimGLCode } from '../SlimGLCode';

import { Shipment } from './Shipment';

export class ShipmentSearchCriteria extends SearchFilter<Shipment> {
  shipmentId: number[] = [];
  scac?: string;
  bolNumber?: string;
  proNumber?: string;
  ownerNaturalIds: string[] = [];
  services: string[] = [];
  modes: string[] = [];
  healths: string[] = [];
  statuses: string[] = [];
  financialStatuses: string[] = [];
  manifestNumber?: string;
  manifestStatuses: string[] = [];
  originLocationId?: number;
  originLocationName?: string;
  originLocationCode?: string;
  originCity?: string;
  originState?: string;
  originPostalCode?: string;
  originCountry?: string;
  originLocationGroupIds: string[] = [];
  destinationLocationId?: number;
  destinationLocationName?: string;
  destinationLocationCode?: string;
  destinationCity?: string;
  destinationState?: string;
  destinationPostalCode?: string;
  destinationCountry?: string;
  destinationLocationGroupIds: string[] = [];
  directions: string[] = [];
  billingTerms: string[] = [];
  equipmentTypes: string[] = [];
  expectedPickupDateRange?: ZonedDateTimeRange;
  actualPickupArrivalDateRange?: ZonedDateTimeRange;
  actualPickupDepartureDateRange?: ZonedDateTimeRange;
  scheduledPickupDateRange?: ZonedDateTimeRange;
  requestedPickupDateRange?: ZonedDateTimeRange;
  requestedPickupDateRangeToExclude?: ZonedDateTimeRange;
  pickupAppointmentRequired: boolean | null = null;
  expectedDeliveryDateRange?: ZonedDateTimeRange;
  actualDeliveryArrivalDateRange?: ZonedDateTimeRange;
  actualDeliveryDepartureDateRange?: ZonedDateTimeRange;
  scheduledDeliveryDateRange?: ZonedDateTimeRange;
  requestedDeliveryDateRange?: ZonedDateTimeRange;
  deliveryAppointmentRequired: boolean | null = null;
  createdDateRange?: ZonedDateTimeRange;
  savedSearchId?: number;
  productNumber?: string;
  multistop?: boolean | null = null;
  referenceNumbers: ReferenceNumber[] = [];
  glCodes: SlimGLCode[] = [];
  tags: string[] = [];
  assignedUsers: UserRef[] = [];
  hasParcelPickupAppointment: boolean | null = null;
  hot: boolean | null = null;
  hazmat: boolean | null = null;
  payable: boolean | null = null;
  freightAllowanceEnabled: boolean | null = null;
  freightAllowance?: MoneySearchFilter;
  costEstimate?: MoneySearchFilter;
  cancellationReason?: string;
  referenceSort?: boolean = false;
  requestedMode: Mode[] = [];
  weight?: UomSearchFilter;
  handlingUnit?: UomSearchFilter;
  source?: string;
  swc: boolean | null = null;
  customerRouted: boolean | null = null;

  constructor(opts?: Partial<ShipmentSearchCriteria>) {
    super(opts);

    if (opts?.shipmentId != null) {
      // There are legacy saved links where shipmentId is not an array that we need to convert here
      this.shipmentId = Array.isArray(opts.shipmentId)
        ? opts.shipmentId
        : [opts.shipmentId];
    }
    if (opts?.bolNumber != null) {
      this.bolNumber = opts.bolNumber;
    }
    if (opts?.proNumber != null) {
      this.proNumber = opts.proNumber;
    }
    if (opts?.ownerNaturalIds != null) {
      this.ownerNaturalIds = opts.ownerNaturalIds;
    }
    if (opts?.services != null) {
      this.services = opts.services;
    }
    if (opts?.modes != null) {
      this.modes = opts.modes;
    }
    if (opts?.equipmentTypes != null) {
      this.equipmentTypes = opts.equipmentTypes;
    }
    if (opts?.statuses != null) {
      this.statuses = opts.statuses;
    }
    if (opts?.manifestNumber != null) {
      this.manifestNumber = opts.manifestNumber;
    }
    if (opts?.manifestStatuses != null) {
      this.manifestStatuses = opts.manifestStatuses;
    }
    if (opts?.financialStatuses != null) {
      this.financialStatuses = opts.financialStatuses;
    }
    if (opts?.scac != null) {
      this.scac = opts.scac;
    }
    if (opts?.originLocationId != null) {
      this.originLocationId = opts.originLocationId;
    }
    if (opts?.originLocationName != null) {
      this.originLocationName = opts.originLocationName;
    }
    if (opts?.originLocationCode != null) {
      this.originLocationCode = opts.originLocationCode;
    }
    if (opts?.originCity != null) {
      this.originCity = opts.originCity;
    }
    if (opts?.originState != null) {
      this.originState = opts.originState;
    }
    if (opts?.originPostalCode != null) {
      this.originPostalCode = opts.originPostalCode;
    }
    if (opts?.originCountry != null) {
      this.originCountry = opts.originCountry;
    }
    if (opts?.originLocationGroupIds != null) {
      this.originLocationGroupIds = [...opts.originLocationGroupIds];
    }
    if (opts?.destinationLocationId != null) {
      this.destinationLocationId = opts.destinationLocationId;
    }
    if (opts?.destinationLocationName != null) {
      this.destinationLocationName = opts.destinationLocationName;
    }
    if (opts?.destinationLocationCode != null) {
      this.destinationLocationCode = opts.destinationLocationCode;
    }
    if (opts?.destinationCity != null) {
      this.destinationCity = opts.destinationCity;
    }
    if (opts?.destinationState != null) {
      this.destinationState = opts.destinationState;
    }
    if (opts?.destinationPostalCode != null) {
      this.destinationPostalCode = opts.destinationPostalCode;
    }
    if (opts?.destinationCountry != null) {
      this.destinationCountry = opts.destinationCountry;
    }
    if (opts?.destinationLocationGroupIds != null) {
      this.destinationLocationGroupIds = [...opts.destinationLocationGroupIds];
    }
    if (opts?.directions != null) {
      this.directions = opts.directions;
    }
    if (opts?.billingTerms != null) {
      this.billingTerms = opts.billingTerms;
    }
    if (opts?.expectedPickupDateRange != null) {
      this.expectedPickupDateRange = new ZonedDateTimeRange(
        opts.expectedPickupDateRange,
      );
    }
    if (opts?.actualPickupArrivalDateRange != null) {
      this.actualPickupArrivalDateRange = new ZonedDateTimeRange(
        opts.actualPickupArrivalDateRange,
      );
    }
    if (opts?.actualPickupDepartureDateRange != null) {
      this.actualPickupDepartureDateRange = new ZonedDateTimeRange(
        opts.actualPickupDepartureDateRange,
      );
    }
    if (opts?.requestedPickupDateRange != null) {
      this.requestedPickupDateRange = new ZonedDateTimeRange(
        opts.requestedPickupDateRange,
      );
    }
    if (opts?.requestedPickupDateRangeToExclude != null) {
      this.requestedPickupDateRangeToExclude = new ZonedDateTimeRange(
        opts.requestedPickupDateRangeToExclude,
      );
    }
    if (opts?.scheduledPickupDateRange != null) {
      this.scheduledPickupDateRange = new ZonedDateTimeRange(
        opts.scheduledPickupDateRange,
      );
    }
    if (opts?.pickupAppointmentRequired != null) {
      this.pickupAppointmentRequired = opts?.pickupAppointmentRequired;
    }
    if (opts?.expectedDeliveryDateRange != null) {
      this.expectedDeliveryDateRange = new ZonedDateTimeRange(
        opts.expectedDeliveryDateRange,
      );
    }
    if (opts?.actualDeliveryArrivalDateRange != null) {
      this.actualDeliveryArrivalDateRange = new ZonedDateTimeRange(
        opts.actualDeliveryArrivalDateRange,
      );
    }
    if (opts?.actualDeliveryDepartureDateRange != null) {
      this.actualDeliveryDepartureDateRange = new ZonedDateTimeRange(
        opts.actualDeliveryDepartureDateRange,
      );
    }
    if (opts?.requestedDeliveryDateRange != null) {
      this.requestedDeliveryDateRange = new ZonedDateTimeRange(
        opts.requestedDeliveryDateRange,
      );
    }
    if (opts?.scheduledDeliveryDateRange != null) {
      this.scheduledDeliveryDateRange = new ZonedDateTimeRange(
        opts.scheduledDeliveryDateRange,
      );
    }
    if (opts?.deliveryAppointmentRequired != null) {
      this.deliveryAppointmentRequired = opts?.deliveryAppointmentRequired;
    }
    if (opts?.createdDateRange != null) {
      this.createdDateRange = new ZonedDateTimeRange(opts.createdDateRange);
    }
    if (opts?.healths != null) {
      this.healths = opts.healths;
    }
    if (opts?.savedSearchId != null) {
      this.savedSearchId = opts.savedSearchId;
    }
    if (opts?.productNumber != null) {
      this.productNumber = opts.productNumber;
    }
    if (opts?.multistop != null) {
      this.multistop = opts?.multistop;
    }
    if (opts?.referenceNumbers != null) {
      this.referenceNumbers = opts.referenceNumbers.map(
        (r) => new ReferenceNumber(r),
      );
    }
    if (opts?.glCodes != null) {
      this.glCodes = opts.glCodes.map((g) => new SlimGLCode(g));
    }
    if (opts?.tags != null) {
      this.tags = opts.tags;
    }
    if (opts?.assignedUsers != null) {
      // Full users carry avatar URLs into the search query param, which freezes the page with many selected
      this.assignedUsers = opts.assignedUsers
        .filter(notNull)
        .map(
          ({ id, username, displayName }) =>
            new UserRef({ id, username, displayName }),
        );
    }
    if (opts?.hasParcelPickupAppointment != null) {
      this.hasParcelPickupAppointment = opts.hasParcelPickupAppointment;
    }
    if (opts?.hot != null) {
      this.hot = opts?.hot;
    }
    if (opts?.hazmat != null) {
      this.hazmat = opts?.hazmat;
    }
    if (opts?.payable != null) {
      this.payable = opts?.payable;
    }
    if (opts?.freightAllowanceEnabled != null) {
      this.freightAllowanceEnabled = opts?.freightAllowanceEnabled;
    }
    if (opts?.freightAllowance != null) {
      this.freightAllowance = new MoneySearchFilter(opts.freightAllowance);
    }
    if (opts?.costEstimate != null) {
      this.costEstimate = new MoneySearchFilter(opts.costEstimate);
    }
    if (opts?.cancellationReason != null) {
      this.cancellationReason = opts?.cancellationReason;
    }
    if (opts?.referenceSort != null) {
      this.referenceSort = opts?.referenceSort;
    }
    if (opts?.requestedMode != null) {
      this.requestedMode = opts.requestedMode;
    }
    if (opts?.weight != null) {
      this.weight = new UomSearchFilter(opts.weight);
    }
    if (opts?.handlingUnit != null) {
      this.handlingUnit = new UomSearchFilter(opts.handlingUnit);
    }
    if (opts?.source != null) {
      this.source = opts.source;
    }
    if (opts?.swc != null) {
      this.swc = opts?.swc;
    }
    if (opts?.customerRouted != null) {
      this.customerRouted = opts?.customerRouted;
    }

    this.sortField = opts?.sortField ?? 'id';
    this.sortDirection = opts?.sortDirection ?? 'DESC';
  }

  get ids(): number[] {
    return this.shipmentId;
  }

  set ids(ids: number[]) {
    this.shipmentId = ids;
  }

  get isShipmentSearchCriteria(): true {
    return true;
  }

  static isShipmentSearchCriteria(obj: unknown): obj is ShipmentSearchCriteria {
    return (
      !!obj && (obj as ShipmentSearchCriteria).isShipmentSearchCriteria === true
    );
  }
}
